import { ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import { useI18n } from 'next-localization';

import { getSearchResults, SearchModifier, SearchParameters } from '@gn-audio/jss-core';

import { SearchContext } from '../providers';
import { transformSearchResultsWithEOL } from '../utils';

type PaginationProps = {
  totalProducts?: number;
  searchQuery: string;
  nextLabel: string;
  previousLabel: string;
  numberOfResultsPerPage: number;
};

const navigationStyle = 'flex justify-center pt-1.5 pb-1.5 pl-3 pr-3 border border-gray-50';
const pageNumberStyle = `${navigationStyle} w-12`;
const disabledNavigationStyle = 'disabled:opacity-50 cursor-default';
const navigationPreviousRoundedStyle = `rounded-bl-md rounded-tl-md`;
const navigationNextRoundedStyle = 'rounded-br-md rounded-tr-md';

export const Pagination = ({
  totalProducts = 0,
  searchQuery,
  nextLabel,
  previousLabel,
  numberOfResultsPerPage,
}: PaginationProps): ReactElement => {
  const numberOfVisiblePages = 3;
  const { searchFilter, searchResults, setSearchResults } = useContext(SearchContext);
  const { locale: getLocale } = useI18n();
  const actualTotalProducts = totalProducts - numberOfResultsPerPage;
  const distanceBetweenEdges = Math.floor(numberOfVisiblePages / 2);
  const numberOfPages = useMemo(
    () => Math.ceil(actualTotalProducts / numberOfResultsPerPage) + 1,
    [actualTotalProducts, numberOfResultsPerPage]
  );

  const [skippedCounter, setSkippedCounter] = useState(0);

  const activePage =
    numberOfPages - Math.ceil((actualTotalProducts - skippedCounter) / numberOfResultsPerPage);

  const [currentPage, setCurrentPage] = useState(activePage);
  const [startingNumberPages, setStartingNumberPages] = useState(
    currentPage <= distanceBetweenEdges ? 1 : currentPage - 2
  );

  const pagesArray = Array.from(
    { length: numberOfVisiblePages },
    (_, i) => startingNumberPages + i
  );
  const [arrayPages, setArrayPages] = useState(pagesArray);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(activePage === 0 ? 1 : activePage);
  }, [activePage]);

  useMemo(() => {
    setStartingNumberPages(
      currentPage <= distanceBetweenEdges ? 1 : currentPage - distanceBetweenEdges
    );
  }, [distanceBetweenEdges, currentPage]);

  useEffect(() => {
    if (currentPage + distanceBetweenEdges <= numberOfPages) {
      setArrayPages(
        Array.from({ length: numberOfVisiblePages }, (_, i) => startingNumberPages + i)
      );
    }
  }, [numberOfVisiblePages, distanceBetweenEdges, numberOfPages, startingNumberPages, currentPage]);

  const handleSearch = async (
    searchQuery: string,
    skipped: number,
    resultsPerPage: number,
    locale: string
  ) => {
    const searchParams: SearchParameters = {
      productSearchFilter: searchFilter,
      searchQuery: searchQuery,
      searchModifier: SearchModifier.Prioritize,
      skip: skipped,
      numberOfResultsPerPage: resultsPerPage,
      locale: locale,
      filterLowStateWithoutSupportUrl: true,
    };
    const result = await getSearchResults(searchParams);
    const resultEOLFiltered = transformSearchResultsWithEOL(result);
    return resultEOLFiltered;
  };

  const onClickNext = (locale: string, step = 1) => {
    const skipped = skippedCounter + step * (searchResults ? searchResults.items.length : 0);
    setSkippedCounter(skipped > actualTotalProducts ? actualTotalProducts : skipped);
    handleSearch(searchQuery, skipped, numberOfResultsPerPage, locale).then((response) => {
      if (setSearchResults) {
        setSearchResults(response);
      }
    });
  };

  const onClickPrev = (locale: string, step = 1) => {
    const skipped = skippedCounter - step * (searchResults ? searchResults.items.length : 0);
    setSkippedCounter(skipped > actualTotalProducts ? actualTotalProducts : skipped);
    handleSearch(searchQuery, skipped, numberOfResultsPerPage, locale).then((response) => {
      if (setSearchResults) {
        setSearchResults(response);
      }
    });
  };

  return (
    <div className="mb-14 flex">
      <button
        role="button"
        className={
          currentPage === 1
            ? `${navigationStyle} ${navigationPreviousRoundedStyle} ${disabledNavigationStyle}`
            : `${navigationStyle} ${navigationPreviousRoundedStyle}`
        }
        disabled={currentPage === 1}
        onClick={() => onClickPrev(getLocale())}
      >
        {previousLabel}
      </button>
      {arrayPages.map((page) => (
        <button
          role="button"
          key={`pagination-${page}`}
          className={
            page > numberOfPages
              ? 'hidden'
              : page === currentPage
                ? `${pageNumberStyle} text-primary`
                : `${pageNumberStyle}`
          }
          onClick={() =>
            page > currentPage
              ? onClickNext(getLocale(), page - currentPage)
              : onClickPrev(getLocale(), currentPage - page)
          }
          disabled={page === currentPage || page > numberOfPages}
        >
          {page}
        </button>
      ))}
      <button
        role="button"
        className={
          currentPage === numberOfPages
            ? `${navigationStyle} ${navigationNextRoundedStyle} ${disabledNavigationStyle}`
            : `${navigationStyle} ${navigationNextRoundedStyle}`
        }
        onClick={() => onClickNext(getLocale())}
        disabled={currentPage === numberOfPages}
      >
        {nextLabel}
      </button>
    </div>
  );
};
